<template>
  <div class="excel-import">
    <input type="file" ref="fileInput" class="hidden" accept=".xlsx, .xls" @change="handleClick">
    <div
        @click="$refs.fileInput.click()"
        @drop="handleDrop"
        @dragover="handleDragover"
        @dragenter="handleDragover"
        class="px-8 py-16 cursor-pointer text-center border-2 border-dashed d-theme-border-grey-light d-theme-dark-bg text-xl">
      <feather-icon icon="UploadCloudIcon" svgClasses="h-16 w-16 stroke-current text-grey" class="block"/>
      <span>Drop Excel File or </span>
      <span class="font-medium text-primary" @click.stop="$refs.fileInput.click()">Browse</span>
    </div>
  </div>
</template>

<script>
import XLSX from 'xlsx'

export default {
  props: {
    onSuccess: {
      type: Function,
      required: true
    }
  },
  data() {
    return {
      excelData: {
        header: null,
        results: null,
        meta: null
      }
    }
  },
  methods: {
    debugLog(step, payload = {}) {
      // eslint-disable-next-line no-console
      console.log(`[AccountingImport][ImportExcel] ${step}`, payload)
    },
    generateData({header, results, meta}) {
      this.debugLog('generateData:start', {
        fileName: meta && meta.fileName,
        importType: meta && meta.importType,
        headerCount: header ? header.length : 0,
        rowCount: results ? results.length : 0
      })
      setTimeout(() => {
        this.$vs.loading.close()
      }, 150);
      this.excelData.header = header
      this.excelData.results = results
      this.excelData.meta = meta
      this.debugLog('generateData:success', { fileName: meta && meta.fileName })
      if (this.onSuccess) this.onSuccess(this.excelData)
    },
    getHeaderRow(sheet) {
      // console.log("getHeaderRow")
      const headers = []
      const range = XLSX.utils.decode_range(sheet['!ref'])
      let C = undefined
      const R = range.s.r
      /* start in the first row */
      for (C = range.s.c; C <= range.e.c; ++C) { /* walk every column in the range */
        const cell = sheet[XLSX.utils.encode_cell({c: C, r: R})]
        /* find the cell in the first row */
        let hdr = `UNKNOWN ${C}` // <-- replace with your desired default
        if (cell && cell.t) hdr = XLSX.utils.format_cell(cell)
        headers.push(hdr)
      }
      return headers
    },
    handleDragover(e) {
      e.stopPropagation()
      e.preventDefault()
      e.dataTransfer.dropEffect = 'copy'
    },
    handleDrop(e) {
      e.stopPropagation()
      e.preventDefault()
      const files = e.dataTransfer.files
      if (files.length !== 1) {
        this.$vs.notify({
          title: 'Login Attempt',
          text: 'Only support uploading one file!',
          iconPack: 'feather',
          icon: 'icon-alert-circle',
          color: 'warning'
        })
        return
      }
      const rawFile = files[0] // only use files[0]
      if (!this.isExcel(rawFile)) {
        this.$vs.notify({
          title: 'Login Attempt',
          text: 'Only supports upload .xlsx, .xls, .csv suffix files',
          iconPack: 'feather',
          icon: 'icon-alert-circle',
          color: 'warning'
        })
        return false
      }
      this.debugLog('handleDrop:fileAccepted', { fileName: rawFile.name, fileSize: rawFile.size })
      this.uploadFile(rawFile)
    },
    readerData(rawFile) {
      return new Promise((resolve, reject) => {
        const startedAt = Date.now()
        this.debugLog('readerData:start', { fileName: rawFile.name, fileSize: rawFile.size })
        const reader = new FileReader();
        reader.onload = e => {
          try {
            const data = e.target.result;
            this.debugLog('readerData:fileLoaded', {
              fileName: rawFile.name,
              elapsedMs: Date.now() - startedAt
            })
            const workbook = XLSX.read(data, { type: 'array' });
            const firstSheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[firstSheetName];
            const header = this.getHeaderRow(worksheet);
            const results = XLSX.utils.sheet_to_json(worksheet, { defval: "" });
            this.debugLog('readerData:sheetParsed', {
              fileName: rawFile.name,
              sheetName: firstSheetName,
              headerCount: header.length,
              rowCount: results.length,
              elapsedMs: Date.now() - startedAt
            })

            const importTypes = {
              "contracts-": "Contracts",
              "contractIndexes-": "ContractIndexes",
              "loanDetails-": "LoanDetails",
              "loanDetailPlans-": "LoanDetailPlans",
              "payments-": "Payments",
              "trackDownDebts-": "TrackDownDebts",
              "contractsSureties-": "ContractsSureties",
              "badDebts-": "BadDebts",
            };

            let matched = false
            for (const prefix in importTypes) {
              if (rawFile.name.startsWith(prefix)) {
                matched = true
                const meta = { sheetName: firstSheetName, fileName: rawFile.name, importType: importTypes[prefix] };
                this.debugLog('readerData:importTypeMatched', { fileName: rawFile.name, prefix, importType: importTypes[prefix] })
                this.generateData({ header, results, meta });
                resolve();
                break;
              }
            }

            if (!matched) {
              this.debugLog('readerData:importTypeNotMatched', { fileName: rawFile.name })
              reject(new Error(`ไม่พบประเภทไฟล์นำเข้า: ${rawFile.name}`))
            }
          } catch (error) {
            this.debugLog('readerData:parseError', { fileName: rawFile.name, error: error && error.message })
            reject(error)
          }
        };
        reader.onerror = () => {
          this.debugLog('readerData:fileReadError', { fileName: rawFile.name })
          reject(new Error(`ไม่สามารถอ่านไฟล์ได้: ${rawFile.name}`))
        }
        reader.readAsArrayBuffer(rawFile);
      });
    },
    handleClick(e) {
      this.$vs.loading()
      const files = e.target.files
      const rawFile = files[0]
      this.debugLog('handleClick:fileSelected', {
        fileName: rawFile && rawFile.name,
        fileSize: rawFile && rawFile.size
      })
      if (!rawFile) return
      this.uploadFile(rawFile)
    },
    isExcel(file) {
      return /\.(xlsx|xls|csv)$/.test(file.name)
    },
    uploadFile(file) {
      this.$refs['fileInput'].value = null // fix can't select the same Excel
      this.debugLog('uploadFile:start', { fileName: file.name })
      this.readerData(file).catch(error => {
        this.debugLog('uploadFile:error', { fileName: file.name, error: error && error.message })
        this.$vs.loading.close()
        this.$vs.notify({
          title: 'Import Error',
          text: error && error.message ? error.message : 'เกิดข้อผิดพลาดระหว่างอ่านไฟล์',
          iconPack: 'feather',
          icon: 'icon-alert-circle',
          color: 'danger'
        })
      })
    }

  }
}
</script>
